import { microServicesRoutes } from '@constants';
import { syncApi } from '@lib/apiClients.ts';
import { type OutreachAccount } from '../../../types/integrations/outreach/outreachTypes.ts';
import { type ApiResponse } from '@types';
import { getCurrentUser } from '@features/auth';
import { logInfo } from '@lib/logger/logInfo.ts';
import { useTranslation } from 'react-i18next';
import { Integrations } from '@features/Settings/enums/IntegrationsEnums.ts';
import { handleError } from '@utils/handleAPIError.ts';

export default function UseOutreachApi ():
{
  getAllOutreachAccounts: (userId: string, organizationId: string) => Promise<ApiResponse<OutreachAccount[]>>,
  loginToOutreach: () => Promise<void>,
  removeOutreachAccount: (organizationId: string, userId: string, nonce: string) => Promise<ApiResponse<void>>
  } {
  const { t } = useTranslation();

  const loginToOutreach = async (): Promise<void> => {
    const currentUser = getCurrentUser();
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }
    const { userId, organizationId } = currentUser;
    const response = await linkOutreachAccount(userId, organizationId);

    if (response.data === undefined) {
      throw new Error('No data returned from link for Outreach Account.');
    }
    const oAuthUrl = response.data.url;

    const popup = window.open(
      oAuthUrl,
      'outreachPopup',
      `noopener,width=${500},height=${500}`
    );

    if (popup !== null) {
      popup.window.addEventListener('unload', ()=> {
        void logInfo(
          t('settings.integrationsPages.loadingMessage', { integrationName: Integrations.Outreach }),
          'linkOutreachAccount',
          { component: UseOutreachApi.name },
          true
        );
      });
    }
  };
    
  async function getAllOutreachAccounts (
    userId: string, organizationId: string
  ): Promise<ApiResponse<OutreachAccount[]>> {
    try {
      const url = `${microServicesRoutes.sync.get.outreach.getLinkedAccounts}/${organizationId}/${userId}`;

      const { data } = await syncApi.get<OutreachAccount[]>(url);

      return { 
        success: true,
        data
      };
    } catch (e: unknown) {
      return handleError(
        'Error while getting outreach accounts.',
        e, 
        'getOutreachAccounts'
      );
    }
  }
  
  async function linkOutreachAccount (
    userId: string, organizationId: string
  ): Promise<ApiResponse<{url:string}>> {
    try {
      const url = `${microServicesRoutes.sync.post.outreach.oAuthLogin}/${organizationId}/${userId}`;
      return await syncApi.post(url);
    } catch (e: unknown) {
      return handleError(
        'Error while linking outreach account.',
        e, 
        'linkOutreachAccount'
      );
    }
  }

  async function removeOutreachAccount (organizationId: string, userId: string, nonce: string): Promise<ApiResponse<void>> {
    try {
      const url = `${microServicesRoutes.sync.delete.outreach.deleteLinkedAccount}/${organizationId}/${userId}/${nonce}`;
      return await syncApi.delete(url);
    } catch (e: unknown) {
      return handleError(
        'Error while deleting outreach account.',
        e,
        'deleteOutreachAccount'
      );
    }
  }
    
  return { getAllOutreachAccounts, loginToOutreach, removeOutreachAccount };
}